import { BackLight } from '@repo/go-icons';
import { ConfigProvider, Loader } from '@repo/go-ui';
import { observer } from 'mobx-react-lite';
import { useState, useEffect } from 'react';
import { I18nextProvider } from 'react-i18next';

import { useServiceAccessor } from 'src/packages/di';
import 'src/packages/assets/styles/index.scss';
import { AccessDeniedModal } from 'src/pages/dashboard/features/access-denied-modal/AccessDeniedModal';
import { ConnectionClosedModal } from 'src/pages/dashboard/features/connection-closed-modal/ConnectionClosedModal';

import type { FC } from 'react';

import styles from './App.module.scss';
import { getIsTokenExpired } from './auth/auth';
import { Auth } from './auth/AuthStore';
import { UserService } from './auth/user-service';
import i18n from './i18n';
import { RootStore, RootStoreProvider } from './root-store';
import { initAppServices } from './root-store/initAppServices';
import Router from './router/Router';
import { getLocale } from './router/utils';

async function auth(): Promise<Auth.UserInfo | undefined> {
  if (getIsTokenExpired()) {
    await UserService.initUserServiceWithCheck();
  } else {
    await UserService.initUserService();
  }

  try {
    const userInfo = await UserService.getUserData();

    if (Auth.isUserInfo(userInfo)) {
      return userInfo;
    } else {
      console.error('Invalid user object');
    }
  } catch (error) {
    console.error(error);
  }
}

export const App: FC = observer(function App() {
  const [store, setStore] = useState<RootStore | null>(null);
  const [error, setError] = useState<'Forbidden' | 'Unknown' | null>(null);

  const getPreloadService = useServiceAccessor('preloadService');
  const getTheme = useServiceAccessor('theme');
  const getLanguage = useServiceAccessor('language');
  const getAuthService = useServiceAccessor('authService');

  useEffect(() => {}, [getTheme]);

  useEffect(() => {
    async function init(): Promise<void> {
      const userInfo = await auth();

      if (!userInfo) {
        return;
      }

      const message = await initAppServices({ userInfo });
      if (message === 'Forbidden') {
        setError('Forbidden');
        return;
      }
      if (message === 'Unknown') {
        setError('Unknown');
        return;
      }

      const preloadService = getPreloadService();

      await preloadService.preload();

      setStore(new RootStore());
    }

    init();
  }, [getPreloadService, getTheme]);

  if (error === 'Forbidden') {
    return (
      <ConfigProvider
        config={{
          theme: getTheme().theme,
          style: 'bordered',
          localization: getLocale(getLanguage().language),
          size: 's',
        }}
        externalElement={document.body}
      >
        <ConnectionClosedModal
          isOpened={true}
          titleT="modals:AccessDenied.title"
          descT="modals:AccessDenied.desc"
          externalAction={() => getAuthService().logout()}
          externalActionT="modals:AccessDenied.bactToLogin"
          externalActionIcon={<BackLight />}
        />
      </ConfigProvider>
    );
  }

  if (error === 'Unknown') {
    return (
      <ConfigProvider
        config={{
          theme: 'dark',
          style: 'bordered',
          size: 's',
        }}
        externalElement={document.body}
      >
        <AccessDeniedModal
          isOpened={true}
          titleT="modals:AccessDeniedUnknown.title"
          descT="modals:AccessDeniedUnknown.desc"
        />
      </ConfigProvider>
    );
  }

  if (!store) {
    return (
      <ConfigProvider
        config={{
          theme: 'dark',
          style: 'bordered',
          size: 's',
        }}
        externalElement={document.body}
      >
        <div className={styles.loaderContainer}>
          <Loader size="l" />
        </div>
      </ConfigProvider>
    );
  }

  return (
    <RootStoreProvider store={store}>
      <I18nextProvider i18n={i18n}>
        <Router />
      </I18nextProvider>
    </RootStoreProvider>
  );
});
